// Waiter-takes-order page: pick a table, build a cart, send it to the
// kitchen. The order auto-attaches to the table's open order (if there
// is one), else opens a new tab.
//
// Design choices that differ from the customer-facing MenuPage:
//   * No language toggle - waiters know the menu in the primary language.
//   * Unavailable items are shown grayed out, NOT hidden, so the waiter
//     can tell a guest "we're out of doro wat tonight" instead of being
//     confused why something they remember isn't on the screen.
//   * After submit, the cart clears but the table stays selected so a
//     waiter can quickly add a second round on the same table.
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  fetchMenu,
  fetchOpenTabs,
  fetchTables,
  fetchMyShifts,
  getCurrency,
  getRole,
  hasFeature,
  placeStaffOrder,
  isDeviceMode,
  lockToPinScreen,
  resolveImageUrl,
} from "../services/api";
import { formatPrice } from "../utils/currency";
import { useT } from "../i18n/useT";

// AM/OM are machine drafts (2026-06) — pending native review
const STRINGS = {
  en: {
    modifiers: "Modifiers",
    note_label: "Note for the kitchen (optional)",
    note_ph_allergy: "e.g. allergy: nuts",
    cancel: "Cancel",
    add_to_order: "Add to order",
    page_title: "Captain order",
    intro:
      "Pick a table, build the order, send it to the kitchen. New items attach to that table's open order — close the order from the Tabs page when the guest leaves.",
    err_tables: "Could not load tables.",
    err_menu: "Could not load menu.",
    step_table: "1. Table",
    no_tables: "No tables found.",
    search_table: "Search table #",
    all_areas: "All areas",
    pill_open: "open",
    tab_open_round:
      "Table {num}: tab is open, this will be round {round}. Running total so far: {total}.",
    tab_fresh: "Table {num}: no open order — this round will start a fresh tab.",
    round_label: "Round {n}",
    at_table: "Table {num}",
    sent_to_kitchen: "sent to the kitchen",
    order_id: "(order #{id})",
    dismiss: "Dismiss",
    step_items: "2. Items",
    no_menu: "No menu items yet.",
    search_items: "Search name or #ID",
    all_categories: "All",
    no_match: "No items match your search.",
    not_available: "Not available right now",
    options_one: "{n} option",
    options_other: "{n} options",
    step_cart: "3. Cart ({n})",
    cart_empty: "Tap items above to add them.",
    note_prefix: "note: {note}",
    note_ph_order: "e.g. table 5 wants doro wat less spicy",
    this_round: "This round",
    sending: "Sending…",
    add_to_tab: "Add to Table {num}'s tab",
    open_new_tab: "Open new tab and send",
    clear: "Clear",
    view_cart_one: "🛒 View cart ({n} item)",
    view_cart_other: "🛒 View cart ({n} items)",
    cart: "Cart ({n})",
    close_cart: "Close cart",
    err_submit: "Could not send order. Try again.",
    when_label: "When", when_asap: "ASAP", when_1h: "+1 hr", when_2h: "+2 hr", when_time: "Pick time",
  },
  am: {
    modifiers: "ማስተካከያዎች",
    note_label: "ለኩሽና ማስታወሻ (አማራጭ)",
    note_ph_allergy: "ለምሳሌ አለርጂ፡ ለውዝ",
    cancel: "ሰርዝ",
    add_to_order: "ወደ ትዕዛዝ ጨምር",
    page_title: "ትዕዛዝ ውሰድ",
    intro:
      "ጠረጴዛ ይምረጡ፣ ትዕዛዙን ይገንቡ፣ ወደ ኩሽና ይላኩት። አዲስ ዕቃዎች በዚያ ጠረጴዛ ክፍት ትዕዛዝ ላይ ይያያዛሉ — ደንበኛው ሲወጣ ከትዕዛዞች ገጽ ላይ ትዕዛዙን ዝጋ።",
    err_tables: "ጠረጴዛዎችን መጫን አልተቻለም።",
    err_menu: "ሜኑን መጫን አልተቻለም።",
    step_table: "1. ጠረጴዛ",
    no_tables: "ምንም ጠረጴዛ አልተገኘም።",
    pill_open: "ክፍት",
    tab_open_round:
      "ጠረጴዛ {num}፡ ትዕዛዝ ክፍት ነው፣ ይህ ዙር {round} ይሆናል። እስካሁን ድምር በሂደት፡ {total}።",
    tab_fresh: "ጠረጴዛ {num}፡ ክፍት ትዕዛዝ የለም — ይህ ዙር አዲስ ትዕዛዝ ይጀምራል።",
    round_label: "ዙር {n}",
    at_table: "ጠረጴዛ {num}",
    sent_to_kitchen: "ወደ ኩሽና ተልኳል",
    order_id: "(ትዕዛዝ #{id})",
    dismiss: "ዝጋ",
    step_items: "2. ዕቃዎች",
    no_menu: "እስካሁን ምንም የሜኑ ዕቃ የለም።",
    search_items: "በስም ወይም #መለያ ይፈልጉ",
    all_categories: "ሁሉም",
    no_match: "ከፍለጋዎ ጋር የሚዛመድ ዕቃ የለም።",
    not_available: "አሁን አይገኝም",
    options_one: "{n} አማራጭ",
    options_other: "{n} አማራጮች",
    step_cart: "3. ጋሪ ({n})",
    cart_empty: "ለመጨመር ከላይ ያሉትን ዕቃዎች ይንኩ።",
    note_prefix: "ማስታወሻ፡ {note}",
    note_ph_order: "ለምሳሌ ጠረጴዛ 5 ዶሮ ወጥ ብዙ ቅመም የለሽ ይፈልጋል",
    this_round: "ይህ ዙር",
    sending: "በመላክ ላይ…",
    add_to_tab: "ወደ ጠረጴዛ {num} ትዕዛዝ ጨምር",
    open_new_tab: "አዲስ ትዕዛዝ ክፈትና ላክ",
    clear: "አጽዳ",
    view_cart_one: "🛒 ጋሪ ይመልከቱ ({n} ዕቃ)",
    view_cart_other: "🛒 ጋሪ ይመልከቱ ({n} ዕቃዎች)",
    cart: "ጋሪ ({n})",
    close_cart: "ጋሪ ዝጋ",
    err_submit: "ትዕዛዝ መላክ አልተቻለም። እንደገና ይሞክሩ።",
    when_label: "መቼ", when_asap: "አሁን", when_1h: "+1 ሰዓት", when_2h: "+2 ሰዓት", when_time: "ሰዓት ምረጥ",
  },
  om: {
    modifiers: "Sirreeffamoota",
    note_label: "Yaadannoo kuusaa nyaataaf (filannoo)",
    note_ph_allergy: "fkn alarjii: lawuza",
    cancel: "Dhiisi",
    add_to_order: "Ajaja itti dabali",
    page_title: "Ajaja fudhadhu",
    intro:
      "Minjaala filadhu, ajaja ijaari, kuusaa nyaataatti ergi. Meeshaaleen haaraa ajaja minjaala sanaa banaa irratti maxxanu — yeroo maamilli ba'u fuula Ajajootaa irraa ajaja cufi.",
    err_tables: "Minjaalota fe'uun hin danda'amne.",
    err_menu: "Meenuu fe'uun hin danda'amne.",
    step_table: "1. Minjaala",
    no_tables: "Minjaalli hin argamne.",
    pill_open: "banaa",
    tab_open_round:
      "Minjaala {num}: ajajni banaadha, kun marsaa {round} ta'a. Ida'amni adeemaa hanga ammaa: {total}.",
    tab_fresh: "Minjaala {num}: ajajni banaan hin jiru — marsaan kun ajaja haaraa eegala.",
    round_label: "Marsaa {n}",
    at_table: "Minjaala {num}",
    sent_to_kitchen: "kuusaa nyaataatti ergame",
    order_id: "(ajaja #{id})",
    dismiss: "Cufi",
    step_items: "2. Meeshaalee",
    no_menu: "Hanga ammaa meeshaaleen meenuu hin jiran.",
    search_items: "Maqaa yookiin #ID barbaadi",
    all_categories: "Hunda",
    no_match: "Barbaachaan kee wajjin kan walsimu hin jiru.",
    not_available: "Amma hin argamu",
    options_one: "filannoo {n}",
    options_other: "filannoolee {n}",
    step_cart: "3. Gaarii ({n})",
    cart_empty: "Itti dabaluuf meeshaalee gubbaa tuqi.",
    note_prefix: "yaadannoo: {note}",
    note_ph_order: "fkn minjaala 5 doroo wat qarxii xiqqaa barbaada",
    this_round: "Marsaa kana",
    sending: "Ergaa jira…",
    add_to_tab: "Ajaja Minjaala {num}tti dabali",
    open_new_tab: "Ajaja haaraa banii ergi",
    clear: "Haqi",
    view_cart_one: "🛒 Gaarii ilaali (meeshaa {n})",
    view_cart_other: "🛒 Gaarii ilaali (meeshaalee {n})",
    cart: "Gaarii ({n})",
    close_cart: "Gaarii cufi",
    err_submit: "Ajaja erguun hin danda'amne. Irra deebi'ii yaali.",
    when_label: "Yeroo", when_asap: "Amma", when_1h: "+1 sa'a", when_2h: "+2 sa'a", when_time: "Yeroo filadhu",
  },
};

// Dietary tag chips (mirrors the customer MenuPage so a waiter sees
// the same allergen info the guest is reading). Keep the slug list in
// sync with backend DIETARY_TAGS.
const TAG_DISPLAY = {
  vegan:            { label: "Vegan",     color: "#1b6b1e", icon: "🌱" },
  vegetarian:       { label: "Veg",       color: "#3a8a3d", icon: "🥗" },
  fasting:          { label: "Fasting",   color: "#5a7a3d", icon: "🙏" },
  gluten_free:      { label: "GF",        color: "#a6611a", icon: "🌾" },
  dairy_free:       { label: "Dairy-free",color: "#5a4fcf", icon: "🥛" },
  nut_free:         { label: "Nut-free",  color: "#8a5a00", icon: "🥜" },
  halal:            { label: "Halal",     color: "#1a7a6b", icon: "" },
  kosher:           { label: "Kosher",    color: "#3a6db8", icon: "" },
  spicy:            { label: "Spicy",     color: "#b13c38", icon: "🌶" },
  contains_alcohol: { label: "Alc.",      color: "#6b3a8a", icon: "🍷" },
};

// Build a stable key for an item-in-cart that distinguishes between the
// same menu item with different modifier selections (so adding a burger
// "no onion" and a plain burger creates two cart rows, not one).
function cartKey(menuId, modifiers) {
  if (!modifiers || !modifiers.length) return String(menuId);
  const names = modifiers.map((m) => m.name).sort().join("|");
  return `${menuId}::${names}`;
}

function lineTotal(line) {
  const modSurcharge = (line.modifiers || []).reduce(
    (s, m) => s + Number(m.price || 0),
    0
  );
  return (Number(line.price || 0) + modSurcharge) * line.quantity;
}

// Tracks viewport width so the cart can stack under the menu on phones
// instead of overlapping it (fix for friend-test feedback #77).
function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" && window.innerWidth < breakpoint
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}


// ---- Modifier picker shown inline when an item has modifiers --------
function ModifierSheet({ item, onCancel, onAdd }) {
  const mods = item.modifiers || [];
  const [picked, setPicked] = useState({});
  const [note, setNote] = useState("");
  // Sub-component reads currency directly — no parent scope here.
  const currency = getCurrency();
  const { lang } = useT();
  const t = (k) => (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;

  const toggle = (m) => {
    setPicked((p) => ({ ...p, [m.name]: !p[m.name] }));
  };

  const submit = () => {
    const selected = mods.filter((m) => picked[m.name]);
    onAdd({
      modifiers: selected,
      notes: note.trim() || null,
    });
  };

  return (
    <div className="modal-backdrop" style={modalBackdrop}>
      <div className="card" style={{ maxWidth: 420, width: "100%" }}>
        <h3 style={{ marginTop: 0 }}>{item.name}</h3>
        <p className="muted" style={{ marginTop: 0 }}>
          {formatPrice(item.price, currency)}{item.description ? ` — ${item.description}` : ""}
        </p>

        {mods.length > 0 && (
          <>
            <p style={{ marginBottom: 6, fontWeight: 600 }}>{t("modifiers")}</p>
            <div style={{ display: "grid", gap: 6, marginBottom: 12 }}>
              {mods.map((m) => (
                <label
                  key={m.name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: 8,
                    background: picked[m.name] ? "#fff8e7" : "#f7f5ef",
                    borderRadius: 8,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={!!picked[m.name]}
                    onChange={() => toggle(m)}
                  />
                  <span style={{ flex: 1 }}>{m.name}</span>
                  {m.price > 0 && (
                    <span className="muted">+{formatPrice(m.price, currency)}</span>
                  )}
                </label>
              ))}
            </div>
          </>
        )}

        <div className="field">
          <label>{t("note_label")}</label>
          <input
            type="text"
            placeholder={t("note_ph_allergy")}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button className="btn secondary" type="button" onClick={onCancel}>
            {t("cancel")}
          </button>
          <button className="btn" type="button" onClick={submit}>
            {t("add_to_order")}
          </button>
        </div>
      </div>
    </div>
  );
}

const modalBackdrop = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 16,
  zIndex: 50,
};


// ---- Main page -----------------------------------------------------
export default function TakeOrderPage() {
  const currency = getCurrency();
  const { lang } = useT();
  const t = (k) => (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;
  const [tables, setTables] = useState([]);
  const [tablesErr, setTablesErr] = useState(null);
  const [menu, setMenu] = useState([]);
  const [menuErr, setMenuErr] = useState(null);
  const [openTabs, setOpenTabs] = useState([]);
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [tquery, setTquery] = useState("");
  const [pickArea, setPickArea] = useState("all");
  const [catFilter, setCatFilter] = useState("all");   // menu category filter
  const [mquery, setMquery] = useState("");             // menu search (name or #ID)
  // Auto-default the area filter to the waiter's scheduled area for today
  // (Hotel-tier scheduling). Applied once, after tables load; never overrides
  // a manual change.
  const autoAreaRef = useRef(false);
  useEffect(() => {
    if (autoAreaRef.current) return;
    if (getRole() !== "waiter" || !hasFeature("scheduling")) return;
    const areaVals = new Set(tables.map((x) => x.area).filter(Boolean));
    if (areaVals.size === 0) return;
    autoAreaRef.current = true;
    const today = new Date().toISOString().slice(0, 10);
    fetchMyShifts(today)
      .then((shifts) => {
        const a = ((shifts || []).find((s) => s.area) || {}).area;
        if (a && areaVals.has(a)) setPickArea(a);
      })
      .catch(() => {});
  }, [tables]);
  const [cart, setCart] = useState({});
  const [orderNote, setOrderNote] = useState("");
  const [whenMode, setWhenMode] = useState("asap");   // asap | 1h | 2h | time
  const [whenAt, setWhenAt] = useState("");            // datetime-local value
  const [modifierItem, setModifierItem] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [submitErr, setSubmitErr] = useState(null);
  const isMobile = useIsMobile();
  // Mobile cart is hidden behind a floating "View cart" pill at the
  // viewport bottom. Tapping it expands the cart in a full-screen sheet.
  // Auto-closes when the cart empties (after a successful submit).
  const [cartOpen, setCartOpen] = useState(false);

  const reloadTabs = () =>
    fetchOpenTabs()
      .then(setOpenTabs)
      .catch(() => {});

  useEffect(() => {
    fetchTables()
      .then(setTables)
      .catch((e) => setTablesErr(e?.response?.data?.detail || t("err_tables")));
    fetchMenu()
      .then(setMenu)
      .catch((e) => setMenuErr(e?.response?.data?.detail || t("err_menu")));
    reloadTabs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-dismiss the mobile cart sheet once the cart empties — happens
  // both on Clear and after a successful submit (which resets the cart).
  useEffect(() => {
    if (Object.keys(cart).length === 0 && cartOpen) setCartOpen(false);
  }, [cart, cartOpen]);

  const openTabForSelected = useMemo(
    () =>
      selectedTableId
        ? openTabs.find((tb) => tb.table_id === selectedTableId)
        : null,
    [openTabs, selectedTableId]
  );

  const categories = useMemo(
    () => Array.from(new Set(menu.map((m) => m.category || "Other"))).sort((a, b) => a.localeCompare(b)),
    [menu]
  );
  // Group by category, applying the category filter + search (name or #ID).
  const grouped = useMemo(() => {
    const q = mquery.trim().toLowerCase();
    const qid = q.replace(/^#/, "");
    const g = {};
    menu.forEach((m) => {
      const cat = m.category || "Other";
      if (catFilter !== "all" && cat !== catFilter) return;
      if (q) {
        const num = m.code != null ? m.code : m.id;
        const nameHit = (m.name || "").toLowerCase().includes(q);
        const idHit = String(num) === qid || String(num).includes(qid);
        if (!nameHit && !idHit) return;
      }
      if (!g[cat]) g[cat] = [];
      g[cat].push(m);
    });
    return g;
  }, [menu, catFilter, mquery]);

  const cartLines = Object.values(cart);
  const cartTotal = cartLines.reduce((s, l) => s + lineTotal(l), 0);
  const cartCount = cartLines.reduce((s, l) => s + l.quantity, 0);

  const addToCart = (item, { modifiers = [], notes = null } = {}) => {
    setSuccess(null);
    setSubmitErr(null);
    const key = cartKey(item.id, modifiers);
    setCart((c) => {
      const existing = c[key];
      const next = existing
        ? { ...existing, quantity: existing.quantity + 1 }
        : {
            key,
            menu_id: item.id,
            name: item.name,
            price: item.price,
            quantity: 1,
            modifiers,
            notes,
          };
      return { ...c, [key]: next };
    });
  };

  const onItemTap = (item) => {
    if (!item.available) return;
    if (item.modifiers && item.modifiers.length) {
      setModifierItem(item);
    } else {
      addToCart(item);
    }
  };

  const onModifierAdd = ({ modifiers, notes }) => {
    if (modifierItem) addToCart(modifierItem, { modifiers, notes });
    setModifierItem(null);
  };

  const updateQty = (key, delta) => {
    setCart((c) => {
      const line = c[key];
      if (!line) return c;
      const newQty = line.quantity + delta;
      if (newQty <= 0) {
        const { [key]: _gone, ...rest } = c;
        return rest;
      }
      return { ...c, [key]: { ...line, quantity: newQty } };
    });
  };

  const removeLine = (key) => {
    setCart((c) => {
      const { [key]: _gone, ...rest } = c;
      return rest;
    });
  };

  const clearCart = () => {
    setCart({});
    setOrderNote("");
    setWhenMode("asap");
    setWhenAt("");
  };

  // Resolve the "when" control to an ISO string (or null = ASAP).
  const requestedAtISO = () => {
    if (whenMode === "asap") return null;
    if (whenMode === "1h") return new Date(Date.now() + 3600e3).toISOString();
    if (whenMode === "2h") return new Date(Date.now() + 7200e3).toISOString();
    if (whenMode === "time" && whenAt) return new Date(whenAt).toISOString();
    return null;
  };

  // Compact scheduling control, shared by both cart footers.
  const renderWhen = () => {
    const chip = (key, label) => (
      <button type="button" onClick={() => setWhenMode(key)}
        style={{ padding: "5px 10px", borderRadius: 16, border: "1px solid " + (whenMode === key ? "#8a6d3b" : "#d3c8af"),
                 background: whenMode === key ? "#8a6d3b" : "#fff", color: whenMode === key ? "#fff" : "#5c4a28",
                 fontSize: 13, cursor: "pointer" }}>{label}</button>
    );
    return (
      <div className="field" style={{ marginTop: 10 }}>
        <label>{t("when_label")}</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
          {chip("asap", t("when_asap"))}
          {chip("1h", t("when_1h"))}
          {chip("2h", t("when_2h"))}
          {chip("time", t("when_time"))}
          {whenMode === "time" && (
            <input type="datetime-local" value={whenAt} onChange={(e) => setWhenAt(e.target.value)}
              style={{ padding: "5px 8px", border: "1px solid #d3c8af", borderRadius: 8 }} />
          )}
        </div>
      </div>
    );
  };

  const submit = async () => {
    if (!selectedTableId || cartLines.length === 0) return;
    setSubmitting(true);
    setSubmitErr(null);
    try {
      const placed = await placeStaffOrder({
        tableId: selectedTableId,
        items: cartLines.map((l) => ({
          menu_id: l.menu_id,
          quantity: l.quantity,
          notes: l.notes,
          modifiers: l.modifiers,
        })),
        notes: orderNote.trim() || null,
        requestedAt: requestedAtISO(),
      });
      clearCart();
      await reloadTabs();
      // round_number now comes from the server -- counts within the tab,
      // resets when the tab is closed and a fresh one opens.
      setSuccess({
        orderId: placed.id,
        roundNumber: placed.round_number || 1,
        total: placed.total,
        tableNumber: placed.table_number,
      });
      // Shared device: return to the lock screen after a beat so the next
      // waiter's order is attributed to them, not this one.
      if (isDeviceMode()) {
        setTimeout(() => { lockToPinScreen(); window.location.href = "/lock"; }, 1800);
      }
    } catch (e) {
      setSubmitErr(
        e?.response?.data?.detail ||
          t("err_submit")
      );
    } finally {
      setSubmitting(false);
    }
  };


  // -------------- render --------------
  const selectedTable = tables.find((tb) => tb.id === selectedTableId);

  return (
    <>
      <h1>{t("page_title")}</h1>
      <p className="muted" style={{ marginTop: 0 }}>
        {t("intro")}
      </p>

      {tablesErr && <div className="error">{tablesErr}</div>}
      {menuErr && <div className="error">{menuErr}</div>}

      {/* Table picker -- horizontal scrollable row of pills */}
      <div className="card" style={{ marginBottom: 16 }}>
        <h3 style={{ marginTop: 0 }}>{t("step_table")}</h3>
        {tables.length === 0 ? (
          <p className="muted">{t("no_tables")}</p>
        ) : (
          <>
            {(() => {
              const areas = Array.from(new Set(tables.map((x) => x.area).filter(Boolean))).sort((a, b) => a.localeCompare(b));
              return areas.length > 1 ? (
                <div className="tabs" style={{ margin: "0 0 10px", flexWrap: "wrap" }}>
                  <div className={"tab " + (pickArea === "all" ? "active" : "")} onClick={() => setPickArea("all")}>{t("all_areas")}</div>
                  {areas.map((a) => (
                    <div key={a} className={"tab " + (pickArea === a ? "active" : "")} onClick={() => setPickArea(a)}>{a}</div>
                  ))}
                </div>
              ) : null;
            })()}
            <input value={tquery} onChange={(e) => setTquery(e.target.value)} placeholder={t("search_table")} style={{ padding: "8px 10px", border: "1px solid #d3c8af", borderRadius: 8, marginBottom: 10, width: 160, maxWidth: "100%" }} />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {tables.filter((x) => (pickArea === "all" || x.area === pickArea) && (!tquery.trim() || String(x.table_number ?? "").includes(tquery.trim()))).map((tbl) => {
              const tab = openTabs.find((x) => x.table_id === tbl.id);
              const isSelected = tbl.id === selectedTableId;
              return (
                <button
                  key={tbl.id}
                  type="button"
                  onClick={() => setSelectedTableId(tbl.id)}
                  style={{
                    minWidth: 70,
                    padding: "10px 14px",
                    borderRadius: 10,
                    border: isSelected
                      ? "2px solid #008080"
                      : "1px solid #d3c8af",
                    background: isSelected
                      ? "#008080"
                      : tab
                      ? "#fff8e7"
                      : "#fff",
                    color: isSelected ? "#fff" : "#1F2933",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  <div style={{ fontSize: "1.1rem" }}>{tbl.table_number}</div>
                  {tab && (
                    <div
                      style={{
                        fontSize: "0.7rem",
                        opacity: 0.85,
                        fontWeight: 500,
                      }}
                    >
                      {t("pill_open")} · {formatPrice(tab.total || 0, currency)}
                    </div>
                  )}
                </button>
              );
            })}
            </div>
          </>
        )}
        {selectedTable && (
          <p className="muted" style={{ marginTop: 12, marginBottom: 0 }}>
            {openTabForSelected
              ? t("tab_open_round")
                  .replace("{num}", selectedTable.table_number)
                  .replace("{round}", openTabForSelected.round_count + 1)
                  .replace("{total}", formatPrice(openTabForSelected.total || 0, currency))
              : t("tab_fresh").replace("{num}", selectedTable.table_number)}
          </p>
        )}
      </div>

      {/* Success banner */}
      {success && (
        <div
          className="success"
          style={{ marginBottom: 16 }}
        >
          <b>{t("round_label").replace("{n}", success.roundNumber)}</b>
          {success.tableNumber != null && (
            <> <b>{t("at_table").replace("{num}", success.tableNumber)}</b></>
          )}
          {" "}{t("sent_to_kitchen")} — {formatPrice(success.total || 0, currency)}.
          <span className="muted" style={{ marginLeft: 8, fontSize: "0.8rem" }}>
            {t("order_id").replace("{id}", success.orderId)}
          </span>
          <button
            className="btn secondary small"
            style={{ marginLeft: 12 }}
            onClick={() => setSuccess(null)}
            type="button"
          >
            {t("dismiss")}
          </button>
        </div>
      )}

      {selectedTableId && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "minmax(0,1fr) minmax(280px,360px)",
            gap: 16,
          }}
        >
          {/* Menu grid */}
          <div>
            <div className="card" style={{ marginBottom: 16 }}>
              <h3 style={{ marginTop: 0 }}>{t("step_items")}</h3>
              {/* Category filter + search (find by name or #ID a guest read off the menu) */}
              {categories.length > 1 && (
                <div className="tabs" style={{ margin: "0 0 8px", flexWrap: "wrap" }}>
                  <div className={"tab " + (catFilter === "all" ? "active" : "")} onClick={() => setCatFilter("all")}>{t("all_categories")}</div>
                  {categories.map((c) => (
                    <div key={c} className={"tab " + (catFilter === c ? "active" : "")} onClick={() => setCatFilter(c)}>{c}</div>
                  ))}
                </div>
              )}
              <input value={mquery} onChange={(e) => setMquery(e.target.value)} placeholder={t("search_items")}
                style={{ padding: "8px 10px", border: "1px solid #d3c8af", borderRadius: 8, marginBottom: 12, width: 240, maxWidth: "100%" }} />
              {menu.length === 0 ? (
                <p className="muted">{t("no_menu")}</p>
              ) : Object.keys(grouped).length === 0 ? (
                <p className="muted">{t("no_match")}</p>
              ) : null}
              {Object.entries(grouped).map(([cat, items]) => (
                <div key={cat} style={{ marginBottom: 16 }}>
                  <h4 style={{ margin: "8px 0 6px" }}>{cat}</h4>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(160px,1fr))",
                      gap: 8,
                    }}
                  >
                    {items.map((it) => {
                      const dimmed = !it.available;
                      const img = resolveImageUrl(it.image_url);
                      const tags = Array.isArray(it.dietary_tags) ? it.dietary_tags : [];
                      return (
                        <button
                          key={it.id}
                          type="button"
                          disabled={dimmed}
                          onClick={() => onItemTap(it)}
                          title={dimmed ? t("not_available") : (it.description || "")}
                          style={{
                            textAlign: "left",
                            border: "1px solid #d3c8af",
                            background: dimmed ? "#f5f1ea" : "#fff",
                            opacity: dimmed ? 0.45 : 1,
                            cursor: dimmed ? "not-allowed" : "pointer",
                            padding: 0,
                            borderRadius: 10,
                            overflow: "hidden",
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          {/* Photo or placeholder — matches guest view */}
                          <div
                            style={{
                              width: "100%",
                              aspectRatio: "16 / 10",
                              background: "#f0ece6",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#b9b2a4",
                              fontSize: 28,
                              position: "relative",
                            }}
                          >
                            {/* Item ID badge — guests reference this from the printed/app menu */}
                            <span style={{ position: "absolute", top: 4, left: 4, background: "rgba(6,59,72,0.85)", color: "#fff", fontSize: "0.66rem", fontWeight: 700, padding: "1px 6px", borderRadius: 6, zIndex: 1 }}>#{it.code != null ? it.code : it.id}</span>
                            {img ? (
                              <img
                                src={img}
                                alt=""
                                loading="lazy"
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                onError={(e) => { e.currentTarget.style.display = "none"; }}
                              />
                            ) : (
                              <span>{it.name?.slice(0, 1) || "🍽"}</span>
                            )}
                          </div>
                          <div style={{ padding: "8px 10px 10px" }}>
                            <div style={{ fontWeight: 600, fontSize: "0.92rem", lineHeight: 1.2 }}>
                              {it.name}
                            </div>
                            {it.description && (
                              <div
                                className="muted"
                                style={{
                                  fontSize: "0.74rem",
                                  marginTop: 2,
                                  display: "-webkit-box",
                                  WebkitLineClamp: 2,
                                  WebkitBoxOrient: "vertical",
                                  overflow: "hidden",
                                }}
                              >
                                {it.description}
                              </div>
                            )}
                            {/* Dietary tag chips so waiter can answer
                                allergen questions without flipping screens */}
                            {tags.length > 0 && (
                              <div
                                style={{
                                  display: "flex",
                                  flexWrap: "wrap",
                                  gap: 3,
                                  marginTop: 4,
                                }}
                              >
                                {tags.map((slug) => {
                                  const tag = TAG_DISPLAY[slug];
                                  if (!tag) return null;
                                  return (
                                    <span
                                      key={slug}
                                      title={tag.label}
                                      style={{
                                        background: tag.color,
                                        color: "#fff",
                                        fontSize: "0.65rem",
                                        padding: "1px 5px",
                                        borderRadius: 999,
                                        fontWeight: 600,
                                      }}
                                    >
                                      {tag.icon ? `${tag.icon} ` : ""}
                                      {tag.label}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                            <div
                              className="muted"
                              style={{
                                fontSize: "0.82rem",
                                marginTop: 6,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                              }}
                            >
                              <span style={{ color: "#063B48", fontWeight: 700 }}>
                                {formatPrice(it.price, currency)}
                              </span>
                              {it.modifiers && it.modifiers.length > 0 && (
                                <span style={{ fontSize: "0.72rem" }}>
                                  {(it.modifiers.length === 1 ? t("options_one") : t("options_other")).replace("{n}", it.modifiers.length)}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cart drawer — desktop only (side panel, sticky as the waiter
              scrolls the menu). On mobile the cart is hidden here and lives
              in a floating bar + modal at the bottom of the viewport (see
              after the grid). */}
          {!isMobile && (
          <div>
            <div
              className="card"
              style={{ marginBottom: 16, position: "sticky", top: 16 }}
            >
              <h3 style={{ marginTop: 0 }}>{t("step_cart").replace("{n}", cartCount)}</h3>
              {cartLines.length === 0 ? (
                <p className="muted">{t("cart_empty")}</p>
              ) : (
                <>
                  <div style={{ display: "grid", gap: 8 }}>
                    {cartLines.map((l) => (
                      <div
                        key={l.key}
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr auto auto auto auto",
                          gap: 8,
                          alignItems: "center",
                          padding: 6,
                          background: "#fbf9f4",
                          borderRadius: 8,
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 600 }}>{l.name}</div>
                          {l.modifiers && l.modifiers.length > 0 && (
                            <div
                              className="muted"
                              style={{ fontSize: "0.75rem" }}
                            >
                              {l.modifiers.map((m) => m.name).join(", ")}
                            </div>
                          )}
                          {l.notes && (
                            <div
                              style={{
                                fontSize: "0.75rem",
                                fontStyle: "italic",
                                color: "#8a5a00",
                              }}
                            >
                              {t("note_prefix").replace("{note}", l.notes)}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          className="btn secondary small"
                          onClick={() => updateQty(l.key, -1)}
                        >
                          −
                        </button>
                        <span style={{ minWidth: 24, textAlign: "center" }}>
                          {l.quantity}
                        </span>
                        <button
                          type="button"
                          className="btn secondary small"
                          onClick={() => updateQty(l.key, +1)}
                        >
                          +
                        </button>
                        <span
                          style={{
                            minWidth: 60,
                            textAlign: "right",
                            fontWeight: 600,
                          }}
                        >
                          {formatPrice(lineTotal(l), currency)}
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="field" style={{ marginTop: 12 }}>
                    <label>{t("note_label")}</label>
                    <input
                      type="text"
                      placeholder={t("note_ph_order")}
                      value={orderNote}
                      onChange={(e) => setOrderNote(e.target.value)}
                    />
                  </div>

                  {renderWhen()}

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      fontWeight: 700,
                      marginTop: 8,
                      fontSize: "1.1rem",
                    }}
                  >
                    <span>{t("this_round")}</span>
                    <span>{formatPrice(cartTotal, currency)}</span>
                  </div>

                  {submitErr && (
                    <div className="error" style={{ marginTop: 8 }}>
                      {submitErr}
                    </div>
                  )}

                  <div
                    style={{
                      display: "flex",
                      gap: 8,
                      marginTop: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <button
                      type="button"
                      className="btn"
                      disabled={submitting}
                      onClick={submit}
                    >
                      {submitting
                        ? t("sending")
                        : openTabForSelected
                        ? t("add_to_tab").replace("{num}", selectedTable?.table_number)
                        : t("open_new_tab")}
                    </button>
                    <button
                      type="button"
                      className="btn secondary"
                      onClick={clearCart}
                    >
                      {t("clear")}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          )}
        </div>
      )}

      {/* Mobile cart bar — floating, pinned to viewport bottom. Visible
          whenever a table is selected and the cart has items. Tap to
          open the full-cart sheet. */}
      {isMobile && selectedTableId && cartCount > 0 && !cartOpen && (
        <button
          type="button"
          onClick={() => setCartOpen(true)}
          style={{
            position: "fixed",
            left: 12,
            right: 12,
            bottom: 12,
            background: "var(--brand, #0E5C66)",
            color: "#fff",
            border: 0,
            borderRadius: 14,
            padding: "14px 18px",
            fontSize: "1rem",
            fontWeight: 700,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
            boxShadow: "0 6px 22px rgba(0,0,0,0.25)",
            zIndex: 40,
            cursor: "pointer",
          }}
        >
          <span>
            {(cartCount === 1 ? t("view_cart_one") : t("view_cart_other")).replace("{n}", cartCount)}
          </span>
          <span>{formatPrice(cartTotal, currency)} →</span>
        </button>
      )}

      {/* Mobile cart sheet — full-screen modal that holds the same cart
          UI desktop sees in its side panel. */}
      {isMobile && cartOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            zIndex: 60,
          }}
          onClick={() => setCartOpen(false)}
        >
          <div
            className="card"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 520,
              maxHeight: "92vh",
              overflowY: "auto",
              borderTopLeftRadius: 16,
              borderTopRightRadius: 16,
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
              marginBottom: 0,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
              <h3 style={{ margin: 0 }}>{t("cart").replace("{n}", cartCount)}</h3>
              <button
                type="button"
                onClick={() => setCartOpen(false)}
                style={{
                  background: "transparent",
                  border: 0,
                  fontSize: "1.6rem",
                  lineHeight: 1,
                  cursor: "pointer",
                  padding: 4,
                  color: "#666",
                }}
                aria-label="Close cart"
              >
                ×
              </button>
            </div>
            {cartLines.length === 0 ? (
              <p className="muted">{t("cart_empty")}</p>
            ) : (
              <>
                <div style={{ display: "grid", gap: 8 }}>
                  {cartLines.map((l) => (
                    <div
                      key={l.key}
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr auto auto auto auto",
                        gap: 6,
                        alignItems: "center",
                        padding: 6,
                        background: "#fbf9f4",
                        borderRadius: 8,
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 600 }}>{l.name}</div>
                        {l.modifiers && l.modifiers.length > 0 && (
                          <div className="muted" style={{ fontSize: "0.75rem" }}>
                            {l.modifiers.map((m) => m.name).join(", ")}
                          </div>
                        )}
                        {l.notes && (
                          <div style={{ fontSize: "0.75rem", fontStyle: "italic", color: "#8a5a00" }}>
                            {t("note_prefix").replace("{note}", l.notes)}
                          </div>
                        )}
                      </div>
                      <button type="button" className="btn secondary small" onClick={() => updateQty(l.key, -1)}>−</button>
                      <span style={{ minWidth: 24, textAlign: "center" }}>{l.quantity}</span>
                      <button type="button" className="btn secondary small" onClick={() => updateQty(l.key, +1)}>+</button>
                      <span style={{ minWidth: 56, textAlign: "right", fontWeight: 600 }}>
                        {formatPrice(lineTotal(l), currency)}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="field" style={{ marginTop: 12 }}>
                  <label>{t("note_label")}</label>
                  <input
                    type="text"
                    placeholder={t("note_ph_order")}
                    value={orderNote}
                    onChange={(e) => setOrderNote(e.target.value)}
                  />
                </div>

                {renderWhen()}

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontWeight: 700,
                    marginTop: 8,
                    fontSize: "1.1rem",
                  }}
                >
                  <span>{t("this_round")}</span>
                  <span>{formatPrice(cartTotal, currency)}</span>
                </div>

                {submitErr && (
                  <div className="error" style={{ marginTop: 8 }}>{submitErr}</div>
                )}

                <div style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                  <button
                    type="button"
                    className="btn"
                    disabled={submitting}
                    onClick={submit}
                    style={{ flex: 1 }}
                  >
                    {submitting
                      ? t("sending")
                      : openTabForSelected
                      ? t("add_to_tab").replace("{num}", selectedTable?.table_number)
                      : t("open_new_tab")}
                  </button>
                  <button type="button" className="btn secondary" onClick={clearCart}>
                    {t("clear")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {modifierItem && (
        <ModifierSheet
          item={modifierItem}
          onCancel={() => setModifierItem(null)}
          onAdd={onModifierAdd}
        />
      )}
    </>
  );
}
